import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck, Banknote, Scale, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/components/Nav";
import { SEMPLIFICAPP_URL } from "@/data/content";

const lineAnim = (delay) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay },
});

const pills = [
  { icon: Banknote, label: "Incasso puntuale del canone" },
  { icon: ShieldCheck, label: "Indennizzo 100% fino a 3 mensilità" },
  { icon: Scale, label: "Tutela legale inclusa" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative bg-ink text-white overflow-hidden">
      <div className="absolute inset-0 opacity-[0.35] bg-[radial-gradient(ellipse_at_top_right,#1B4268_0%,transparent_55%)]" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-azure/10 blur-3xl" />

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-36 lg:pt-44 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="eyebrow text-azure mb-8"
            data-testid="hero-eyebrow"
          >
            Gestione locazioni · Firenze & Scandicci
          </motion.p>

          <h1 className="font-serif font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-[4.4rem]">
            <span className="masked-line">
              <motion.span {...lineAnim(0.45)}>Il tuo affitto,</motion.span>
            </span>
            <span className="masked-line">
              <motion.span {...lineAnim(0.58)}>
                <em className="text-azure not-italic font-serif italic">garantito.</em>
              </motion.span>
            </span>
            <span className="masked-line">
              <motion.span {...lineAnim(0.71)} className="text-white/60">
                Ogni singolo mese.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-8 max-w-xl text-base lg:text-lg text-white/65 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Affitti residenziali senza pensieri: riscossione puntuale del canone, inquilini
            selezionati, fino a 3 mensilità di indennizzo in caso di morosità e tutela legale
            completa. La casa è tua. Al resto pensiamo noi.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contatti"
              data-testid="hero-cta-calculate-btn"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#contatti");
              }}
              className="group inline-flex items-center gap-2 bg-gold text-ink font-semibold rounded-full px-7 py-4 text-sm sm:text-base hover:bg-white transition-colors duration-300"
            >
              Valuta il tuo immobile gratis
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={SEMPLIFICAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-reserved-app-btn"
              className="inline-flex items-center gap-2 text-white/85 border border-white/25 rounded-full px-7 py-4 text-sm sm:text-base hover:bg-white/10 transition-colors duration-300"
            >
              Accedi a Semplificapp.it
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.35 }}
            className="mt-12 flex flex-wrap gap-3"
          >
            {pills.map((p) => (
              <span
                key={p.label}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs sm:text-sm text-white/75"
              >
                <p.icon size={15} className="text-azure" />
                {p.label}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.9 }}
            className="relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden border border-white/15 shadow-2xl">
              <motion.img
                style={{ y: imgY, scale: 1.15 }}
                src="/assets/hero-casa.jpg"
                alt="Casa residenziale con cielo azzurro gestita con la formula Semplificaffitto"
                className="w-full h-[340px] sm:h-[420px] lg:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <p className="font-serif italic text-lg text-white/90">"Finalmente a casa, senza pensieri."</p>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 1.5 }}
              className="absolute -left-4 sm:-left-10 top-8 rounded-2xl bg-white text-ink shadow-xl px-5 py-4"
              data-testid="hero-stat-card"
            >
              <p className="font-serif font-bold text-3xl text-brandblue">100%</p>
              <p className="text-xs text-ink/60 mt-0.5 max-w-[140px]">del canone indennizzato in caso di morosità, fino a 3 mensilità</p>
            </motion.div>

            <motion.img
              initial={{ opacity: 0, rotate: -20, scale: 0.7 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 1.7, ease: [0.16, 1, 0.3, 1] }}
              src="/assets/takes-care-logo.jpg"
              alt="Semplificaffitto.it Takes Care!"
              className="absolute -right-5 -bottom-6 w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 border-white/25 shadow-2xl animate-[spin_30s_linear_infinite]"
              data-testid="hero-takes-care-stamp"
            />
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.8 }}
        onClick={() => scrollToId("#manifesto")}
        data-testid="hero-scroll-down-btn"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 hover:text-white/80 transition-colors"
        aria-label="Scorri in basso"
      >
        <ArrowDown size={22} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
